import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, Gavel, Heart, Plus, Eye, Trash2 } from "lucide-react";
import { ProductCardCompact } from "@/components/ProductCards";
import api from "@/utils/api";

const NO_IMAGE_PLACEHOLDER = "https://placehold.co/400x400/e2e8f0/64748b?text=No+Image";

const DashboardPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [bids, setBids] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [listingsRes, bidsRes, watchlistRes] = await Promise.all([
          api.get("/my-listings"),
          api.get("/my-bids"),
          api.get("/watchlist")
        ]);
        setListings(listingsRes.data.listings);
        setBids(bidsRes.data.bids);
        setWatchlist(watchlistRes.data.listings);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate]);

  const deleteListing = async (listingId) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    try {
      await api.delete(`/listings/${listingId}`);
      setListings(listings.filter(l => l.listing_id !== listingId));
      toast.success("Listing deleted");
    } catch (error) {
      toast.error("Failed to delete listing");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-52 pb-16 bg-slate-50" data-testid="dashboard-page">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center gap-4 mb-8">
          <Avatar className="h-16 w-16">
            <AvatarImage src={user.picture} />
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl">
              {user.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="font-outfit text-2xl font-bold">{user.name}</h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="listings" data-testid="listings-tab">
              <Package className="h-4 w-4 mr-2" /> My Listings
            </TabsTrigger>
            <TabsTrigger value="bids" data-testid="bids-tab">
              <Gavel className="h-4 w-4 mr-2" /> My Bids
            </TabsTrigger>
            <TabsTrigger value="watchlist" data-testid="watchlist-tab">
              <Heart className="h-4 w-4 mr-2" /> Watchlist
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Your Listings</h2>
              <Button onClick={() => navigate('/sell')} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
                <Plus className="h-4 w-4 mr-2" /> New Listing
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : listings.length > 0 ? (
              <div className="space-y-4">
                {listings.map((listing) => (
                  <Card key={listing.listing_id} className="p-4">
                    <div className="flex gap-4">
                      <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        <img 
                          src={listing.images?.[0] || NO_IMAGE_PLACEHOLDER} 
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold truncate">{listing.title}</h3>
                            <p className="text-sm text-muted-foreground capitalize">{listing.category}</p>
                          </div>
                          <Badge className={`status-badge ${listing.status}`}>{listing.status}</Badge>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <div>
                            <span className="text-lg font-bold text-emerald-600">
                              ${(listing.current_bid || listing.price || 0).toFixed(2)}
                            </span>
                            {listing.listing_type === 'auction' && (
                              <span className="text-sm text-muted-foreground ml-2">
                                {listing.bid_count || 0} bid{listing.bid_count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/item/${listing.listing_id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => deleteListing(listing.listing_id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No listings yet</h3>
                <p className="text-muted-foreground mb-4">Start selling by creating your first listing</p>
                <Button onClick={() => navigate('/sell')} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
                  Create Listing
                </Button>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="bids">
            <h2 className="text-xl font-semibold mb-6">Your Bids</h2>
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : bids.length > 0 ? (
              <div className="space-y-4">
                {bids.map((bid) => (
                  <Card 
                    key={bid.bid_id} 
                    className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/item/${bid.listing_id}`)}
                  >
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        {bid.listing_image ? (
                          <img src={bid.listing_image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{bid.listing_title}</h3>
                        <p className="text-sm text-muted-foreground">
                          Your bid: <span className="font-medium text-foreground">${bid.amount.toFixed(2)}</span>
                        </p>
                        {bid.is_winning && (
                          <Badge className="mt-2 bg-emerald-500">Winning!</Badge>
                        )}
                      </div>
                      <Badge variant="secondary" className={bid.listing_status === 'active' ? 'bg-emerald-100' : ''}>
                        {bid.listing_status}
                      </Badge>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed">
                <Gavel className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No bids yet</h3>
                <p className="text-muted-foreground mb-4">Start bidding on auctions you love</p>
                <Button onClick={() => navigate('/browse?type=auction')} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
                  Browse Auctions
                </Button>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="watchlist">
            <h2 className="text-xl font-semibold mb-6">Your Watchlist</h2>
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : watchlist.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {watchlist.map((listing) => (
                  <ProductCardCompact 
                    key={listing.listing_id} 
                    listing={listing}
                    isWatched={true}
                    onWatchlistToggle={async (id) => {
                      try {
                        await api.delete(`/watchlist/${id}`);
                        setWatchlist(watchlist.filter(w => w.listing_id !== id));
                        toast.success("Removed from watchlist");
                      } catch {}
                    }}
                  />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed">
                <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">Watchlist is empty</h3>
                <p className="text-muted-foreground mb-4">Save items you&apos;re interested in</p>
                <Button onClick={() => navigate('/browse')} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
                  Browse Items
                </Button>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
